import Card from "../../../Card/Card";
import { Button, ButtonOption } from '../../../Fundamentals/Button';
import { RadioButtons } from '../../../FormComponents/RadioButtons';
import { Checkbox } from '../../../FormComponents/Checkbox';
import { Dropdown } from '../../../FormComponents/Dropdown';
import { IntermediarySelector } from '../../../Voss/IntermediarySelector';
import AlertSmall from '../../../AlertSmall';
import DatePicker from '../../../Fundamentals/DatePicker';
import useUserAccess from '../../../../Hooks/useUserAccess';
import { UserRoles } from '../../../../Constants/userRoles';
import { VossProductGroupTagType } from '../../../../Constants/VOSS/vossProductGroupTagType';
import { VossCustomerSubscriptionStatus } from '../../../../Constants/VOSS/vossCustomerSubscriptionStatus';
import { useTranslation } from 'react-i18next';
import { dateUtils } from "../../../../Utils";
import Spinner from '../../../Fundamentals/Spinner/Spinner';

const prepareGroupedProductGroups = (groups, scheduledChange) => {
    const currentRank = {};
    groups.forEach(g => (g.customerSubscriptionStatus === VossCustomerSubscriptionStatus.Active || g.willMigrate)
        && g.tags?.forEach(t => t.type === VossProductGroupTagType.Exclusive && (currentRank[t.tag] = t.rank ?? Infinity)));

    const tagMap = {}, ungrouped = [];
    groups.forEach(g => {
        const excs = (g.tags ?? []).filter(t => t.type === VossProductGroupTagType.Exclusive);
        if (!scheduledChange && g.customerSubscriptionStatus !== VossCustomerSubscriptionStatus.Active && !g.willMigrate && excs.some(t => !(t.rank > (currentRank[t.tag] ?? -Infinity)))) return;
        excs.length ? excs.forEach(t => (tagMap[t.tag] ??= []).push(g)) : ungrouped.push([g]);
    });
    return [...Object.values(tagMap), ...ungrouped];
};

export function VossTierOptionsCard({
    offer,
    selections,
    selectablePlans,
    migrationSourceSubscription,
    loading,
    intermediaries,
    subscription,
    scheduledChange,
    onOpenTierModal,
    onUpdateSelection,
    onToggleOptionalGroup,
    onSelectExclusiveGroup,
}) {
    const { t } = useTranslation();
    const { hasAdminAccess } = useUserAccess();

    const tier = offer.tiers?.find(t => t.id === selections.tierId);
    const optionalGroups = tier?.productGroups?.filter(g => g.productGroupType === 0) ?? [];

    const infoMessage = () => {
        const parts = [];
        if (migrationSourceSubscription)
            parts.push(<>{t('vossTierOptionsCard.migrationMessagePre')} <strong>{migrationSourceSubscription.package.name}</strong> {t('vossTierOptionsCard.migrationMessagePost')}</>);
        if (scheduledChange)
            parts.push(<>{t('vossTierOptionsCard.scheduledChangePre')} {migrationSourceSubscription ? t('vossTierOptionsCard.moved') : t('vossTierOptionsCard.changed')} {t('vossTierOptionsCard.scheduledChangeTo')} <strong>{scheduledChange.tier?.name}</strong> {t('vossTierOptionsCard.scheduledChangeOn')} <strong>{dateUtils.formatDate(scheduledChange.scheduledDate)}</strong>.</>);
        return parts.length > 1 ? <>{parts[0]} {parts[1]}</> : parts[0];
    };

    return (
        <Card>
            <Card.Header>
                <Card.Header.Title>
                    {tier ? `${tier.name}` : ''}
                </Card.Header.Title>
                <Card.Header.Buttons>
                    <Button
                        option={ButtonOption.PRIMARY}
                        onClick={onOpenTierModal}
                    >
                        {t('vossTierOptionsCard.selectTier')}
                    </Button>
                </Card.Header.Buttons>
            </Card.Header>
            {!selections.tierId && (
                <Card.Content>
                    <span className="text-muted">{t('vossTierOptionsCard.noTierSelected')}</span>
                </Card.Content>
            )}
            {selections.tierId && (
                <Card.Content>
                    {loading ? <Spinner /> : (
                        <div className="d-flex flex-column gap-16">
                            {(migrationSourceSubscription || scheduledChange) && (
                                <AlertSmall alert={{ message: infoMessage(), type: "info" }} />
                            )}

                            <div>
                                <h3 className="font-family-ubuntu-semibold mb-8">{t('vossTierOptionsCard.plan')}</h3>
                                <fieldset className="d-grid col mb-0 px-0" disabled={subscription && selectablePlans?.some(p => p.id === subscription.plan?.id)}>
                                    <RadioButtons
                                        items={selectablePlans?.map(plan => ({ label: plan.name, value: plan.id })) ?? []}
                                        value={selections.planId}
                                        onChange={(value) => onUpdateSelection('planId', value)}
                                    />
                                </fieldset>
                            </div>

                            {optionalGroups.length > 0 && (() => {
                                const isGroupDisabled = (group) => group.advisory?.disableSelection === true
                                    || (!scheduledChange
                                        && (group.customerSubscriptionStatus === VossCustomerSubscriptionStatus.Active
                                            || (group.willMigrate && selections.optionalProductGroupIds?.includes(group.id))));
                                
                                const advisoryMessage = (item) => {
                                    const key = item.advisory.translationKeyOverride ?? 'vossTierOptionsCard.addonAdvisoryForTier';
                                    return t(key, {
                                        productGroup: item.name,
                                        currentTier: tier.name,
                                        requiredTier: item.advisory.requiredTierName,
                                    });
                                };

                                const advisoryAlert = (item) => item?.advisory && (
                                    <div className="mt-4">
                                        <AlertSmall alert={{ message: advisoryMessage(item), type: "warning" }} />
                                    </div>
                                );
                                return (
                                    <div>
                                        <h3 className="font-family-ubuntu-semibold mb-8">{t('vossTierOptionsCard.optionalProductGroups')}</h3>
                                        <fieldset className="d-grid col gap-12 mb-0 px-0">
                                            {prepareGroupedProductGroups(optionalGroups, scheduledChange).map((group, index) => {
                                                if (group.length === 1) {
                                                    const item = group[0];
                                                    return (
                                                        <div key={item.id} className="mb-0">
                                                            <Checkbox
                                                                id={`group-${item.id}`}
                                                                label={item.name}
                                                                checked={selections.optionalProductGroupIds?.includes(item.id) ?? false}
                                                                onChange={() => onToggleOptionalGroup(item.id)}
                                                                disabled={isGroupDisabled(item)}
                                                            />
                                                            {advisoryAlert(item)}
                                                        </div>
                                                    );
                                                }

                                                const exclusiveIds = group.map(g => g.id);
                                                const displayedItem = group.find(g => selections.optionalProductGroupIds?.includes(g.id)) ?? group[0];
                                                if (!displayedItem) return null;
                                                const selectedId = displayedItem.id;
                                                const isChecked = group.some(g => selections.optionalProductGroupIds?.includes(g.id));
                                                const dropdownOptions = group.map(g => ({ ...g, disabled: g.advisory?.disableSelection === true }));
                                                
                                                return (
                                                    <div key={`exclusive-${index}`} className="mb-0">
                                                        <div className="d-flex align-items-center">
                                                            <div className="d-flex">
                                                                <Checkbox
                                                                    id={`exclusive-${index}`}
                                                                    checked={isChecked}
                                                                    disabled={displayedItem.advisory?.disableSelection === true
                                                                        || (!scheduledChange && group.some(g => g.customerSubscriptionStatus === VossCustomerSubscriptionStatus.Active || (g.willMigrate && isChecked)))}
                                                                    onChange={(checked) => onSelectExclusiveGroup(checked ? selectedId : null, exclusiveIds)}
                                                                />
                                                            </div>
                                                            <div className="pl-0 col">
                                                                <Dropdown
                                                                    options={dropdownOptions}
                                                                    value={selectedId}
                                                                    onChange={(value) => onSelectExclusiveGroup(value, exclusiveIds)}
                                                                />
                                                            </div>
                                                        </div>
                                                        {advisoryAlert(displayedItem)}
                                                    </div>
                                                );
                                            })}
                                        </fieldset>
                                    </div>
                                );
                            })()}

                            {!subscription && hasAdminAccess(UserRoles.InvoiceDate) && (
                                <div>
                                    <h3 className="font-family-ubuntu-semibold mb-8">{t('vossTierOptionsCard.billingStartDate')}</h3>
                                    <DatePicker
                                        dateFormat={"YYYY-MM"}
                                        showTime={false}
                                        value={selections.billingStartDate}
                                        onChange={(value) => onUpdateSelection('billingStartDate', value)}
                                    />
                                </div>
                            )}
                            {intermediaries && (
                                <div>
                                    <h3 className="font-family-ubuntu-semibold mb-8">{t('vossTierOptionsCard.intermediary')}</h3>
                                    <IntermediarySelector
                                        intermediaries={intermediaries}
                                        onChange={(value) => onUpdateSelection('intermediaryInvoicingId', value)}
                                        value={selections.intermediaryInvoicingId}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Card.Content>
            )}
        </Card>
    );
}